import React from "react";
import { useSelector } from 'react-redux';
import { useTheme, Box, Typography, Chip, Card, CardContent, Divider } from "@mui/material";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { formatTZDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';


const ArrivalCard = React.memo(({ timeMode, theme, idx, f }) => {
    const flightDate = timeMode === "UTC"
        ? formatTZDate(f?.flightActualDayArr || f?.flightDate)
        : formatTZDate(f?.flightActualDayArrLocalBase || f?.flightDateLocalBase);

    const sta = timeMode === "UTC" ? f?.sta : f?.staLocalBase;
    const eta = timeMode === "UTC" ? f?.eta : f?.etaLocalBase;
    const ata = timeMode === "UTC" ? f?.ata : f?.ataLocalBase;

    const delay = f?.arrDelaysTime || 0;
    const delayColor = delay > 60
        ? '#f2292d'
        : delay > 30
            ? '#f29129'
            : delay > 15
                ? '#d7f229'
                : theme.palette.text.primary;

    return (
        <Card
            variant="outlined"
            sx={{
                boxShadow: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.neutral.deep}`,
                bgcolor: theme.palette.background.default,
            }}
        >
            <CardContent sx={{ pt: 1.5, '&:last-child': { pb: 1.5 } }}>
                <FlexBetween>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={idx + 1}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{ fontSize: "0.75rem", borderWidth: 1.5, height: 20, "& .MuiChip-label": { px: 1 } }}
                        />
                        <Typography variant="body1" color={theme.palette.primary.dark}>
                            {f.flightNo}
                        </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                        {flightDate}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {f.flightReg} | {f.acType}
                    </Typography>
                </FlexBetween>

                <FlexBetween mt={0.5}>
                    <Typography display="flex" alignItems="center" variant="body1" color="text.primary">
                        {f.dep} <ArrowRightAltIcon /> {f.arr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Pax: {f.noOfPax ?? '-'}
                    </Typography>
                </FlexBetween>

                <FlexBetween mt={0.5}>
                    <Typography variant="body2" color="text.secondary">Sta: {sta || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Eta: {eta || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">Ata: {ata || '-'}</Typography>
                </FlexBetween>

                {delay > 0 && (
                    <Typography variant="body2" sx={{ color: delayColor, mt: 0.5 }}>
                        Arr Delay: {delay}'
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
});

const DepartureCard = React.memo(({ timeMode, theme, idx, f }) => {
    const flightDate = timeMode === "UTC"
        ? formatTZDate(f?.flightActualDayDep || f?.flightDate)
        : formatTZDate(f?.flightActualDayDepLocalBase || f?.flightDateLocalBase);

    const std = timeMode === "UTC" ? f?.std : f?.stdLocalBase;
    const etd = timeMode === "UTC" ? f?.etd : f?.etdLocalBase;
    const atd = timeMode === "UTC" ? f?.atd : f?.atdLocalBase;

    const delay = f?.depDelaysTime || 0;
    const delayColor = delay > 60
        ? '#f2292d'
        : delay > 30
            ? '#f29129'
            : delay > 15
                ? '#d7f229'
                : theme.palette.text.primary;

    return (
        <Card
            variant="outlined"
            sx={{
                boxShadow: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.neutral.deep}`,
                bgcolor: theme.palette.background.default,
            }}
        >
            <CardContent sx={{ pt: 1.5, '&:last-child': { pb: 1.5 } }}>
                <FlexBetween>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={idx + 1}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{ fontSize: "0.75rem", borderWidth: 1.5, height: 20, "& .MuiChip-label": { px: 1 } }}
                        />
                        <Typography variant="body1" color={theme.palette.primary.dark}>
                            {f.flightNo}
                        </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                        {flightDate}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {f.flightReg} | {f.acType}
                    </Typography>
                </FlexBetween>

                <FlexBetween mt={0.5}>
                    <Typography 
                        display="flex" 
                        alignItems="center" 
                        variant="body1" 
                        color="text.primary"
                    >
                        {f.dep} <ArrowRightAltIcon /> {f.arr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Pax: {f.noOfPax ?? '-'}
                    </Typography>
                </FlexBetween>

                <FlexBetween mt={0.5}>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                    >
                        Std: {std || '-'}
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                    >
                        Etd: {etd || '-'}
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                    >
                        Atd: {atd || '-'}
                    </Typography>
                </FlexBetween>

                {delay > 0 && (
                    <Typography variant="body2" sx={{ color: delayColor, mt: 0.5 }}>
                        Dep Delay: {delay}'
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
});


const StationScheduleList = ({ arrivals = [], departures = [] }) => {
    const theme = useTheme();
    const timeMode = useSelector((state) => state.timemode);
    const primaryDark = theme.palette.primary.dark;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {/* ===== ARRIVALS ===== */}
            <Box display="flex" alignItems="center" gap={1}>
                <FlightLandIcon sx={{ color: primaryDark }} />
                <Typography variant="h6" color={primaryDark}>
                    Arrivals ({arrivals.length})
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
                {arrivals.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No arrivals</Typography>
                ) : (
                    arrivals.map((f, idx) => (
                        <ArrivalCard
                            key={f._id || idx}
                            timeMode={timeMode}
                            theme={theme}
                            idx={idx}
                            f={f}
                        />
                    ))
                )}
            </Box>

            <Divider />

            {/* ===== DEPARTURES ===== */}
            <Box display="flex" alignItems="center" gap={1}>
                <FlightTakeoffIcon sx={{ color: primaryDark }} />
                <Typography variant="h6" color={primaryDark}>
                    Departures ({departures.length})
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
                {departures.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No departures</Typography>
                ) : (
                    departures.map((f, idx) => (
                        <DepartureCard
                            key={f._id || idx}
                            timeMode={timeMode}
                            theme={theme}
                            idx={idx}
                            f={f}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
};

export default React.memo(StationScheduleList);
